import React, { useMemo } from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyCernMode({ brand, today, Copy }: ModeRendererProps) {
  // Deterministic pseudo-random generator based on indices (SSR-safe)
  const rand = (i: number, j: number) => {
    const x = Math.sin(i * 12.9898 + j * 78.233 + 0.5) * 43758.5453123;
    return x - Math.floor(x);
  };

  // Namespaced gradient id
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `cern_${param}` : `cern_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `cern_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);
  const gradId = `${idBase}_grad`;

  // CERN gradient: CERN blue to particle glow
  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#003D7A" />
        <stop offset="50%" stopColor="#0066CC" />
        <stop offset="100%" stopColor="#00FFFF" />
      </linearGradient>
    </defs>
  );

  const collisionLattices = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    for (let row = 0; row < 14; row++) {
      for (let col = 0; col < 10; col++) {
        const x = 80 + col * 64;
        const y = 120 + row * 65;
        const energy = Math.sin(row * 0.5 + col * 0.3) * 0.5 + 0.5;
        nodes.push(
          <circle
            key={`collision-${row}-${col}`}
            cx={x}
            cy={y}
            r={8 + energy * 12}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={2}
            opacity={0.4 + energy * 0.4}
          />
        );
      }
    }
    return nodes;
  }, []);

  const spiralChambers = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    for (let i = 0; i < 8; i++) {
      const radius = 180 + i * 30;
      const cx = 397;
      const cy = 561.5;
      const points: string[] = [];
      for (let j = 0; j < 36; j++) {
        const a = (j * Math.PI * 2) / 36;
        const r = radius * (1 + Math.sin(a * 4) * 0.1);
        points.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
      }
      nodes.push(
        <polyline
          key={`chamber-${i}`}
          points={points.join(" ")}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={1.5}
          opacity={0.3}
        />
      );
    }
    return nodes;
  }, []);

  const scintillationGrids = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    for (let i = 0; i < 20; i++) {
      const y = 100 + i * 48;
      for (let j = 0; j < 12; j++) {
        const x = 70 + j * 60;
        const flash = rand(i, j) > 0.7; // deterministic instead of Math.random()
        if (flash) {
          nodes.push(
            <rect
              key={`scint-${i}-${j}`}
              x={x - 3}
              y={y - 3}
              width={6}
              height={6}
              fill="#00FFFF"
              opacity={0.6}
            />
          );
        }
      }
    }
    return nodes;
  }, []);

  const particleTraces = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    for (let i = 0; i < 16; i++) {
      const x = 80 + i * 44;
      nodes.push(
        <line
          key={`trace-${i}`}
          x1={x}
          y1={100}
          x2={x}
          y2={1023}
          stroke={`url(#${gradId})`}
          strokeWidth={1}
          opacity={0.2}
          strokeDasharray="4,4"
        />
      );
    }
    return nodes;
  }, []);

  // Collision center: Large Hadron Collider core
  const collisionCenter = (
    <g opacity={0.8}>
      <circle cx={397} cy={561.5} r={120} fill="none" stroke="#00FFFF" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={80} fill="none" stroke="#0066CC" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={50} fill="none" stroke="#003D7A" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={25} fill="#00FFFF" opacity={0.3} />
      {/* Beam collision cross */}
      <line x1={277} y1={561.5} x2={517} y2={561.5} stroke="#00FFFF" strokeWidth={2} />
      <line x1={397} y1={441.5} x2={397} y2={681.5} stroke="#00FFFF" strokeWidth={2} />
    </g>
  );

  return (
    <g>
      {defs}
      {particleTraces}
      {collisionLattices}
      {spiralChambers}
      {scintillationGrids}
      {collisionCenter}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}
